import { faClock, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import QRCode from "qrcode"
import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { useTimeClock } from "../../hooks/TimeClock/useTimeClock"
import type { TimeClockQrCode } from "../../Objects/TimeClock/TimeClockQrCode"

export const TimeClockPublicCodePage = () => {
  const { storeSlug } = useParams<{ storeSlug: string }>()

  const { t } = useTranslation("timeClock")

  const { getPublicQrCode } = useTimeClock()

  const [qrCode, setQrCode] = useState<TimeClockQrCode | undefined>(undefined)

  const [qrImage, setQrImage] = useState<string | undefined>(undefined)

  const [isUnavailable, setIsUnavailable] = useState(false)

  const [secondsLeft, setSecondsLeft] = useState(0)

  const load = useCallback(async () => {
    if (!storeSlug) return

    const result = await getPublicQrCode(storeSlug)

    if (result.data) {
      setQrCode(result.data)
      setIsUnavailable(false)
    } else {
      setIsUnavailable(true)
    }
  }, [getPublicQrCode, storeSlug])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load()

    const interval = window.setInterval(() => void load(), 30000)

    return () => window.clearInterval(interval)
  }, [load])

  useEffect(() => {
    if (!qrCode) return

    QRCode.toDataURL(qrCode.token, { margin: 1, width: 320, color: { dark: "#1c1917", light: "#ffffff" } })
      .then(setQrImage)
      .catch(() => setQrImage(undefined))
  }, [qrCode])

  useEffect(() => {
    if (!qrCode) return

    const tick = () => {
      const remaining = Math.max(0, Math.round((new Date(qrCode.expiresAtUtc).getTime() - Date.now()) / 1000))

      setSecondsLeft(remaining)
    }

    tick()

    const interval = window.setInterval(tick, 1000)

    return () => window.clearInterval(interval)
  }, [qrCode])

  const progress = qrCode ? Math.max(0, Math.min(1, secondsLeft / qrCode.intervalSeconds)) : 0

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-white p-3 text-center sm:gap-8 sm:p-6">
      {qrCode && !isUnavailable ? (
        <Animate key={qrCode.token} type={AnimationTypes.zoomIn} duration="0.6s">
          <div className="flex w-full max-w-full flex-col items-center gap-4 rounded-2xl border-4 border-orange-200 bg-gradient-to-br from-orange-50 via-white to-amber-50 p-4 shadow-2xl sm:gap-8 sm:rounded-[2rem] sm:p-10 lg:p-16">
            <div className="flex items-center gap-2 text-orange-700 sm:gap-3">
              <FontAwesomeIcon icon={faClock} className="text-lg sm:text-2xl" />

              <span className="text-sm font-extrabold uppercase tracking-[0.15em] sm:text-lg sm:tracking-[0.3em]">
                {t("public_code.title")}
              </span>
            </div>

            {qrImage && (
              <div className="rounded-2xl border-4 border-orange-100 bg-white p-2 shadow-inner sm:rounded-3xl sm:p-4">
                <img src={qrImage} alt={qrCode.token} className="h-36 w-36 sm:h-56 sm:w-56 lg:h-72 lg:w-72" />
              </div>
            )}

            <div className="max-w-full overflow-hidden rounded-2xl bg-slate-950 px-4 py-3 shadow-inner sm:rounded-3xl sm:px-10 sm:py-6 lg:px-16 lg:py-8">
              <span className="font-mono text-2xl font-black tracking-[0.1em] text-white sm:text-4xl sm:tracking-[0.25em] lg:text-7xl lg:tracking-[0.35em]">
                {qrCode.token}
              </span>
            </div>

            <div className="flex w-full max-w-xs flex-col items-center gap-2">
              <div className="h-3 w-full overflow-hidden rounded-full bg-orange-100">
                <div
                  className="h-full rounded-full bg-orange-500 transition-all duration-1000 ease-linear"
                  style={{ width: `${progress * 100}%` }}
                />
              </div>

              <span className="text-sm font-bold text-orange-700">
                {t("public_code.refresh_label", { seconds: secondsLeft })}
              </span>
            </div>

            <p className="max-w-md text-sm font-semibold text-slate-600 sm:text-base lg:text-lg">
              {t("public_code.subtitle")}
            </p>
          </div>
        </Animate>
      ) : (
        <Animate type={AnimationTypes.fadeInTraslate} duration="0.6s">
          <div className="flex w-full max-w-md flex-col items-center gap-4 rounded-3xl border-4 border-orange-200 bg-orange-50 p-6 text-orange-900 shadow-2xl sm:p-10">
            <FontAwesomeIcon icon={faTriangleExclamation} className="text-3xl sm:text-4xl" />

            <span className="text-lg font-extrabold sm:text-xl">{t("public_code.unavailable_title")}</span>

            <p className="text-sm font-medium">{t("public_code.unavailable_description")}</p>
          </div>
        </Animate>
      )}
    </div>
  )
}
